import { Box } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import timezones from "../../../src/assets/Time Zone/allTimeZone.json";
import CustomAutocomplete from "../../shared/AutoComplete";
import { setTimezoneDetails } from "../../store/features/timezoneSlice";
import { RootState } from "../../store/store";
import { notifyUser } from "../fiscalUpgrade/Utils/notifyUser";

interface TimezoneOption {
  label: string;
  value: string;
}

interface TimeZoneSelectorProps {
  disabled?: boolean;
  defaultValue?: string;
}

const TimeZoneSelector: React.FC<TimeZoneSelectorProps> = ({
  disabled = false,
  defaultValue,
}) => {
  const dispatch = useDispatch();
  const timezoneDetails = useSelector((state: RootState) => state.timezone);
  const [selectedZone, setSelectedZone] = useState<TimezoneOption | null>(null);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;

  const syncFromStore = useCallback(() => {
    if (timezoneDetails?.value) {
      const found = timezones.find((tz) => tz.value === timezoneDetails.value);
      setSelectedZone(found || null);
    } else if (defaultValue) {
      const defaultZone = timezones.find((tz) => tz.value === defaultValue);
      if (defaultZone) {
        setSelectedZone(defaultZone);
        dispatch(setTimezoneDetails(defaultZone));
      }
    }
  }, [timezoneDetails, defaultValue, dispatch]);

  const handleChange = (newValue: TimezoneOption | null) => {
    if (newValue) {
      setSelectedZone(newValue);
      dispatch(setTimezoneDetails(newValue));
      notifyUser(translate("timeZoneUpdatedTo") + newValue.label, "success", translate("timeZone"));
    }
  };

  useEffect(() => {
    syncFromStore();
  }, [syncFromStore]);

  return (
    <Box sx={{ width: "20%", marginTop: 2 }}>
      <CustomAutocomplete
        size="small"
        disabled={disabled}
        options={timezones}
        value={selectedZone}
        onChange={(event, newValue) => handleChange(newValue)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        label={translate("selectTimezone")}
        placeholder={translate("selectTimezone")}
      />
    </Box>
  );
};

export default TimeZoneSelector;
